import { useSearchParams, useLocation } from "react-router-dom";
import {
  ArrowRight, Check, Sparkles, Boxes, Tag, ShoppingCart, Factory, Truck,
  Wallet, Leaf, Crown, GraduationCap, Users, Store, Award, Building2,
  Download, Car, Bus,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { getRegistrationUrl, EVENT_ENDED } from "@/config";

const RegisterBtn = ({ label, lang }) =>
  EVENT_ENDED ? null : (
  <Reveal delay={0.1}>
    <a
      href={getRegistrationUrl(lang)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="about-register-btn"
      className="mt-14 group inline-flex items-center justify-center gap-2 bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-8 py-4 font-semibold tracking-wide transition-colors"
    >
      {label}
      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
    </a>
  </Reveal>
);

const pillarIcons = [GraduationCap, Users, Store, Award];

const SummitTab = ({ d }) => (
  <div className="space-y-14">
    <div className="space-y-5 max-w-3xl">
      {d.paragraphs.map((p, i) => (
        <Reveal key={i} delay={0.05 * i}>
          <p className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-white font-display" : "text-base md:text-lg text-slate-300"}`}>{p}</p>
        </Reveal>
      ))}
    </div>
    <Reveal>
      <p className="font-display text-xl md:text-2xl text-[#CEA26B] font-light tracking-tight">{d.tagline}</p>
    </Reveal>
    <div>
      <Reveal><h3 className="overline text-[#CEA26B]">{d.pillarsIntro}</h3></Reveal>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {d.pillars.map((it, i) => {
          const Icon = pillarIcons[i] || Sparkles;
          return (
            <Reveal key={i} delay={0.05 * i} className="bg-[#010E3A]">
              <div className="p-7 h-full hover:bg-white/[0.03] transition-colors" data-testid={`about-pillar-${i}`}>
                <Icon className="h-7 w-7 text-[#CEA26B]" />
                <h4 className="mt-5 font-display font-bold text-white text-base leading-snug">{it.title}</h4>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
    <div className="max-w-3xl">
      <Reveal><h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{d.whyTitle}</h3></Reveal>
      <div className="mt-6 space-y-5">
        {d.whyParagraphs.map((p, i) => (
          <Reveal key={i} delay={0.05 * i}><p className="text-base md:text-lg text-slate-300 leading-relaxed">{p}</p></Reveal>
        ))}
      </div>
    </div>
  </div>
);

const WhyTab = ({ d }) => (
  <div className="space-y-14">
    <div className="space-y-5 max-w-3xl">
      {d.intro.map((p, i) => (
        <Reveal key={i} delay={0.05 * i}>
          <p className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-white font-display" : "text-base md:text-lg text-slate-300"}`}>{p}</p>
        </Reveal>
      ))}
    </div>
    <div className="space-y-px bg-white/10 border border-white/10">
      {d.benefits.map((it, i) => (
        <Reveal key={i} delay={0.04 * i} className="bg-[#010E3A]">
          <div className="p-7 md:p-8 hover:bg-white/[0.02] transition-colors" data-testid={`about-benefit-${i}`}>
            <div className="flex items-start gap-5">
              <span className="font-display font-bold text-2xl text-[#CEA26B] leading-none shrink-0 w-10">{String(i + 1).padStart(2, "0")}</span>
              <div className="min-w-0">
                <h4 className="font-display font-bold text-white text-lg sm:text-xl leading-snug">{it.title}</h4>
                <p className="mt-3 text-base text-slate-300 leading-relaxed">{it.desc}</p>
                <div className="mt-4 border-l-2 border-[#CEA26B]/60 pl-4">
                  <span className="overline text-[#CEA26B] text-xs">{d.outcomeLabel}</span>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">{it.outcome}</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

const audienceIcons = [Boxes, Tag, ShoppingCart, Factory, Truck, Wallet, Leaf, Crown];

const AudienceTab = ({ d }) => (
  <div className="space-y-14">
    <Reveal><p className="text-lg md:text-xl text-white font-display leading-relaxed max-w-3xl">{d.intro}</p></Reveal>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {d.items.map((it, i) => {
        const Icon = audienceIcons[i] || Boxes;
        return (
          <Reveal key={i} delay={0.04 * i}>
            <div className="group h-full bg-white/[0.02] border border-white/10 p-7 hover:bg-white/[0.05] hover:-translate-y-1 hover:border-[#CEA26B]/40 transition-all duration-300" data-testid={`about-audience-${i}`}>
              <div className="flex items-center justify-center h-12 w-12 border border-white/10 bg-[#CEA26B]/10 group-hover:bg-[#CEA26B]/20 transition-colors">
                <Icon className="h-6 w-6 text-[#CEA26B]" />
              </div>
              <h4 className="mt-6 font-display font-bold text-white text-lg">{it.title}</h4>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    <Reveal>
      <div className="border-l-2 border-[#7E1A2E] pl-6 max-w-3xl">
        <h3 className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight">{d.notForTitle}</h3>
        <p className="mt-3 text-base text-slate-400 leading-relaxed">{d.notForText}</p>
      </div>
    </Reveal>
  </div>
);

const SybaTab = ({ d }) => (
  <div className="space-y-14">
    <div className="space-y-5 max-w-3xl">
      {d.paragraphs.map((p, i) => (
        <Reveal key={i} delay={0.05 * i}><p className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-white font-display" : "text-base md:text-lg text-slate-300"}`}>{p}</p></Reveal>
      ))}
    </div>
    <div className="max-w-3xl">
      <Reveal><h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{d.whyTitle}</h3></Reveal>
      <div className="mt-6 space-y-5">
        {d.whyParagraphs.map((p, i) => (
          <Reveal key={i} delay={0.05 * i}><p className="text-base md:text-lg text-slate-300 leading-relaxed">{p}</p></Reveal>
        ))}
      </div>
    </div>
    <Reveal>
      <div className="border border-[#CEA26B]/30 bg-[#CEA26B]/[0.06] p-7 max-w-3xl flex items-start gap-4">
        <Building2 className="h-7 w-7 text-[#CEA26B] shrink-0" />
        <div>
          <h3 className="font-display font-bold text-white text-lg">{d.organizerTitle}</h3>
          <p className="mt-2 text-base text-slate-300 leading-relaxed">{d.organizerText}</p>
        </div>
      </div>
    </Reveal>
  </div>
);

const VenueTab = ({ d }) => (
  <div>
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed">{d.intro}</p>
          <div className="mt-8 space-y-px bg-white/10 border border-white/10">
            {d.details.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-4 bg-[#010E3A] px-5 py-4" data-testid={`venue-detail-${i}`}>
                <span className="text-sm text-slate-400">{it.label}</span>
                <span className="font-display font-bold text-white text-sm text-right">{it.value}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.1}>
          <div className="relative h-full min-h-[340px] border border-white/10 overflow-hidden">
            <iframe
              title="Hotel Aquapalace Praha"
              src="https://www.google.com/maps?q=Aquapalace%20Hotel%20Prague%2C%20Pra%C5%BEsk%C3%A1%20137%2C%20%C4%8Cestlice&output=embed"
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="venue-map"
            />
          </div>
        </Reveal>
      </div>
    </div>

    {d.accommodation && (
      <Reveal>
        <div className="mt-16 border border-[#CEA26B]/40 bg-[#CEA26B]/[0.06] px-8 py-8 max-w-4xl" data-testid="venue-accommodation">
          <h3 className="font-display font-bold text-white text-2xl tracking-tight">{d.accommodation.title}</h3>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            {d.accommodation.text}{" "}
            <a href={`mailto:${d.accommodation.email}`} className="text-[#CEA26B] font-semibold hover:text-white transition-colors" data-testid="venue-accommodation-email">
              {d.accommodation.email}
            </a>
          </p>
          <a
            href={d.accommodation.formFile}
            download
            data-testid="venue-accommodation-form"
            className="mt-6 inline-flex items-center gap-2.5 bg-[#CEA26B] px-7 py-3.5 text-sm font-bold tracking-wide uppercase text-[#01154F] hover:bg-white transition-colors"
          >
            <Download className="h-4 w-4" />
            {d.accommodation.formLabel}
          </a>
        </div>
      </Reveal>
    )}

    {d.transport && (
      <Reveal>
        <div className="mt-16 max-w-4xl" data-testid="venue-transport">
          <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{d.transport.title}</h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            <div className="bg-[#010E3A] px-6 py-6">
              <div className="flex items-center gap-3">
                <Car className="h-5 w-5 text-[#CEA26B]" />
                <h4 className="font-display font-bold text-white text-lg">{d.transport.carTitle}</h4>
              </div>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{d.transport.carText}</p>
              <h4 className="mt-6 font-display font-bold text-white text-lg">{d.transport.parkingTitle}</h4>
              <ul className="mt-3 space-y-2">
                {d.transport.parkingItems.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                    <span className="h-1.5 w-1.5 bg-[#CEA26B] shrink-0 mt-2" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#010E3A] px-6 py-6">
              <div className="flex items-center gap-3">
                <Bus className="h-5 w-5 text-[#CEA26B]" />
                <h4 className="font-display font-bold text-white text-lg">{d.transport.transferTitle}</h4>
              </div>
              <ul className="mt-3 space-y-2">
                {d.transport.transferItems.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                    <span className="h-1.5 w-1.5 bg-[#CEA26B] shrink-0 mt-2" />
                    {it}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm text-slate-400 leading-relaxed">
                {d.transport.transferNote}{" "}
                <a href={`mailto:${d.transport.transferEmail}`} className="text-[#CEA26B] font-semibold hover:text-white transition-colors">
                  {d.transport.transferEmail}
                </a>
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    )}
  </div>
);

export const About = () => {
  const { t, lang } = useLanguage();
  const a = t.aboutPage;
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();

  const valid = ["summit", "why", "audience", "syba", ...(EVENT_ENDED ? [] : ["venue"])];
  const fromUrl = params.get("tab");
  const initial = pathname.includes("proc-se-zucastnit") && !fromUrl ? "why" : fromUrl;
  const tab = valid.includes(initial) ? initial : "summit";
  const setTab = (k) => setParams(k === "summit" ? {} : { tab: k }, { replace: true });

  const tabs = [
    { key: "summit", label: a.tabs.summit },
    { key: "why", label: a.tabs.why },
    { key: "audience", label: a.tabs.audience },
    { key: "syba", label: a.tabs.syba },
    ...(EVENT_ENDED ? [] : [{ key: "venue", label: a.tabs.venue }]),
  ];

  const d = a[tab];

  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#010E3A] border-y border-white/5">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <span className="overline text-[#CEA26B]">{d.overline}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{d.title}</h2>
        </Reveal>

        <div className="mt-8 inline-flex flex-wrap border border-white/15 p-1 gap-1" data-testid="about-subtabs">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              data-testid={`about-subtab-${tb.key}`}
              className={`px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors ${
                tab === tb.key ? "bg-[#CEA26B] text-[#01154F]" : "text-slate-300 hover:text-white"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-12">
          {tab === "summit" && <SummitTab d={d} />}
          {tab === "why" && <WhyTab d={d} />}
          {tab === "audience" && <AudienceTab d={d} />}
          {tab === "syba" && <SybaTab d={d} />}
          {tab === "venue" && <VenueTab d={d} />}
          <RegisterBtn label={t.hero.ctaTicket} lang={lang} />
        </div>
      </div>
    </section>
  );
};
